export default function BlogContent() {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <article className="prose prose-lg max-w-none">
          <div className="text-gray-700 leading-relaxed space-y-6">
            <p className="text-xl text-gray-600 font-medium">
              Building on a hill requires more than just a standard plan. The dramatic topography of North Vancouver, Coquitlam, and the Tri-Cities presents unique challenges that demand specialized formwork strategies.
            </p>

            <p>
              At <strong>Dinari Designs</strong>, we've spent years mastering the art of hillside construction. Here's what we've learned about designing formwork for sloped sites in BC's most challenging terrain:
            </p>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-landscape-line text-white text-xl"></i>
                </span>
                Understanding the Terrain
              </h2>
              <p className="text-gray-700 mb-0">
                Sloped sites in <a href="/home#contact" className="text-teal-600 hover:text-teal-700 font-semibold">North Vancouver construction</a> projects aren't just "a little uneven"—they often involve significant grade changes, rocky outcroppings, and complex drainage considerations. Our <strong>AutoCAD site plans</strong> start with a thorough topographic analysis to identify these challenges before they become problems.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-bar-chart-grouped-line text-white text-xl"></i>
                </span>
                Accounting for Elevation Changes
              </h2>
              <p className="text-gray-700 mb-0">
                Every foot of elevation change affects your formwork design. We use <strong>AutoCAD</strong> to create detailed section drawings that show exactly how formwork transitions from one level to the next. This includes stepped footings, retaining walls, and multi-level slab pours—all coordinated to work with the natural slope.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-building-4-line text-white text-xl"></i>
                </span>
                Shoring and Support Requirements
              </h2>
              <p className="text-gray-700 mb-0">
                Hillside formwork often requires extensive shoring to support both the formwork itself and the surrounding soil. Our drawings include detailed shoring plans that specify post locations, bracing requirements, and load calculations—ensuring that your formwork stays stable even on steep grades.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-water-flash-line text-white text-xl"></i>
                </span>
                Drainage and Waterproofing Coordination
              </h2>
              <p className="text-gray-700 mb-0">
                Water flows downhill—and on sloped sites, managing that flow is critical. Our <strong>formwork designs</strong> integrate with your drainage and waterproofing plans to ensure that water is directed away from foundations and retaining walls. This coordination prevents costly water damage and foundation issues down the road.
              </p>
            </div>

            <div className="bg-slate-50 border-l-4 border-teal-500 p-6 my-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                <span className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-map-2-line text-white text-xl"></i>
                </span>
                Local Experience in the Tri-Cities and North Shore
              </h2>
              <p className="text-gray-700 mb-0">
                We've worked on hillside projects throughout the Lower Mainland—from the steep slopes of North Vancouver to the rolling terrain of Coquitlam and Port Moody. This local experience means we understand the specific soil conditions, building codes, and construction practices that define <strong>sloped site construction in BC</strong>.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 my-12 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Ready to lean out your overhead?
              </h2>
              <p className="text-lg mb-6 text-teal-50">
                Hillside construction doesn't have to be complicated. With professional <strong>AutoCAD site planning</strong> and formwork design, you can tackle even the steepest slopes with confidence.
              </p>
              <a 
                href="/home#contact" 
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-teal-50 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Get a Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-500">Share this article:</span>
                  <div className="flex space-x-3">
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-linkedin-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-twitter-x-fill"></i>
                    </a>
                    <a href="#" className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-teal-500 hover:text-white transition-colors cursor-pointer">
                      <i className="ri-facebook-fill"></i>
                    </a>
                  </div>
                </div>
                <a 
                  href="/knowledge-hub" 
                  className="text-teal-600 hover:text-teal-700 font-medium flex items-center cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Knowledge Hub
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}
